import Link from "next/link";
import { START_HREF, contactHref } from "@/lib/routes";

/**
 * Shared marketing footer — landing, pricing, and every audience page.
 * One component so a new page or link lands everywhere at once, and so
 * crawlers get consistent paths into the site from every public page.
 *
 * Locale-aware WITHOUT the landing i18n context: audience pages render
 * outside the LanguageProvider, so strings live in a local dict keyed by
 * the same "en" | "nl" locale prop those pages already carry.
 */

type Locale = "en" | "nl";

const STRINGS: Record<Locale, Record<string, string>> = {
  en: {
    tagline: "One score for every Dutch property — 90+ factors, 25+ official sources, in seconds.",
    product: "Product",
    scorePlot: "Score a plot",
    preview: "Preview",
    pricing: "Pricing",
    methodology: "Methodology",
    dataSources: "Data sources",
    amsterdam: "Amsterdam buurt guides",
    audiences: "Who it's for",
    developers: "Developers & investors",
    brokers: "Brokers & advisors",
    homebuyers: "Homebuyers & owners",
    expats: "Expats buying in NL",
    planners: "Municipalities & planners",
    company: "Company",
    contact: "Contact",
    privacy: "Privacy",
    terms: "Terms",
    location: "Built in Amsterdam",
  },
  nl: {
    tagline: "Eén score voor elk Nederlands pand — 90+ factoren, 25+ officiële bronnen, in seconden.",
    product: "Product",
    scorePlot: "Scoor een perceel",
    // Same word in both locales, matching SiteNav's NAV_COPY.
    preview: "Preview",
    pricing: "Prijzen",
    methodology: "Methodologie",
    dataSources: "Databronnen",
    amsterdam: "Amsterdamse buurtgidsen",
    audiences: "Voor wie",
    developers: "Ontwikkelaars & investeerders",
    brokers: "Makelaars & adviseurs",
    homebuyers: "Kopers & eigenaren",
    expats: "Expats buying in NL",
    planners: "Gemeenten & planologen",
    company: "Bedrijf",
    contact: "Contact",
    privacy: "Privacy",
    terms: "Voorwaarden",
    location: "Gemaakt in Amsterdam",
  },
};

interface FooterLink {
  label: string;
  href: string;
  mailto?: boolean;
}

function FooterCol({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <p className="font-data text-[0.62rem] uppercase tracking-[0.22em] text-brand-muted mb-4">
        {title}
      </p>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.href}>
            {l.mailto ? (
              <a
                href={l.href}
                className="text-[0.82rem] text-brand-linen/75 hover:text-brand-meadow transition-colors"
              >
                {l.label}
              </a>
            ) : (
              <Link
                href={l.href}
                className="text-[0.82rem] text-brand-linen/75 hover:text-brand-meadow transition-colors"
              >
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SiteFooter({ locale = "en" }: { locale?: Locale }) {
  const s = STRINGS[locale];
  const nl = locale === "nl";

  return (
    <footer className="border-t border-brand-border bg-brand-pine">
      <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10">
          <div className="col-span-2 md:col-span-1">
            <Link
              href={nl ? "/nl" : "/"}
              className="font-display text-[1.1rem] font-semibold text-brand-linen hover:text-brand-meadow transition-colors"
            >
              PlotScore
            </Link>
            <p className="mt-3 text-[0.8rem] text-brand-muted leading-[1.65] max-w-[240px]">
              {s.tagline}
            </p>
          </div>
          <FooterCol
            title={s.product}
            links={[
              { label: s.scorePlot, href: START_HREF },
              // Preview is a main nav slot and priority 0.9 in sitemap.ts —
              // it belongs in the footer's Product column too, so every page
              // gives crawlers (and readers) a path to it.
              { label: s.preview, href: nl ? "/nl/preview" : "/preview" },
              { label: s.pricing, href: nl ? "/nl/pricing" : "/pricing" },
              { label: s.methodology, href: nl ? "/nl/methodology" : "/methodology" },
              { label: s.dataSources, href: nl ? "/nl/data-sources" : "/data-sources" },
              // The hub is the only door into the 882 buurt guides. It used to
              // be linked from three audience pages and nowhere else, so
              // crawlers reached the whole section through the sitemap alone.
              { label: s.amsterdam, href: nl ? "/nl/amsterdam" : "/amsterdam" },
            ]}
          />
          <FooterCol
            title={s.audiences}
            links={[
              { label: s.developers, href: nl ? "/nl/voor-ontwikkelaars" : "/for-developers" },
              { label: s.brokers, href: nl ? "/nl/voor-makelaars" : "/for-brokers" },
              { label: s.homebuyers, href: nl ? "/nl/voor-kopers" : "/for-homebuyers" },
              // English-only page — same href in both locales (no NL equivalent by design).
              { label: s.expats, href: "/for-expats" },
              { label: s.planners, href: nl ? "/nl/voor-gemeenten" : "/for-planners" },
            ]}
          />
          <FooterCol
            title={s.company}
            links={[
              // /founder is DELIBERATELY not linked here (or anywhere visible):
              // it is an external-search-only surface for the founder's name.
              // Site visitors should not encounter it; Google/AI reach it via
              // sitemap.ts, the sitewide Organization schema and llms.txt.
              // Don't "fix" the orphan page by re-adding a link.
              { label: s.contact, href: contactHref(), mailto: true },
              { label: s.privacy, href: "/legal#privacy" },
              { label: s.terms, href: "/legal#terms" },
            ]}
          />
        </div>
        <div className="mt-10 pt-6 border-t border-brand-border flex flex-col sm:flex-row items-center justify-between gap-3 text-[0.72rem] text-brand-muted">
          <span>{s.location}</span>
          <span>© {new Date().getFullYear()} PlotScore</span>
        </div>
      </div>
    </footer>
  );
}
